// Core packages
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';

// Third party packages
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs';

// Custom packages
import { AuthService } from 'src/app/modules/auth/auth.service';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { HelperService } from 'src/app/shared/services/helper.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  private loggedUser: IUser;
  private sessionExpiresAt: number;
  private lang = this.translateService.currentLang || this.translateService.defaultLang;

  constructor(
    private router: Router,
    private authService: AuthService,
    private helperService: HelperService,
    private cookieService: CookieService,
    private translateService: TranslateService,
  ) { }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
    this.loggedUser = this.authService.loggedUser$.value;
    this.sessionExpiresAt = +this.cookieService.get('s.expiresAt'); // this.authService.sessionExpiresAt$.value;
    this.lang = this.cookieService.get('lang') || this.translateService.currentLang;

    // Check if token is expired
    if (this.loggedUser && this.loggedUser._id && (moment.unix(moment.now()) > moment.unix(this.sessionExpiresAt))) {
      // Users seems to be logged, but his token is expired.
      // Log him out (remove data from cookie) and return false
      this.authService.logout(false);
      return false;
    }

    // Check if user is already logged in authService
    if (this.loggedUser && this.loggedUser._id) {
      // User is logged and his token is not expired. Let's return true.
      return true;
    }

    // Check if users is logged with cookie
    // (if we came here: (i) user is logged but requested
    // brand new page through page refresh, (ii) user is
    // not logged in)
    return new Observable((observer) => {
      this.authService.checkLogin()
        .subscribe(
          res => {
            if (res instanceof Error || !res.status) { // If back-end is down we recive an error!
              this.helperService.handleError(res);
              // this.authService.logout();
              this.router.navigate([this.lang, 'auth', 'login'], { queryParams: { returnUrl: state.url } });
              return observer.next(false);
            }
            // User logged,
            observer.next(true);
          },
          err => {
            // User is not logged in, redirect him
            this.authService.logout();
            this.router.navigate([this.lang, 'auth', 'login'], { queryParams: { returnUrl: state.url } });
            observer.next(false);
          }
        );
    });
  }
}
